// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setFilterDrawerIsOpen from "@west/App/Handlers/XNG/ProductList";
import { CustomAppState } from "@west/App/Store/Reducers";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    view: state.pages.productList.view,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    filterDrawerIsOpen: state.xProductList.filterDrawerIsOpen,
    headerHeight: state.xHeader.headerHeight,
    productFilters: state.pages.productList.productFilters,
});

// XNG-Changes: add mapDispatchToProps
const mapDispatchToProps = {
    setFilterDrawerIsOpen,
    clearAllProductFilters,
};

// XNG-Changes: add mapDispatchToProps to props type
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    // XNG-Changes
    filtersDrawer?: DrawerPresentationProps;
    filterWrapper?: InjectableCss;
    controlsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    closeBtn?: LinkPresentationProps;
    cancelBtn?: ButtonPresentationProps;
    applyBtn?: ButtonPresentationProps;
    title?: TypographyProps;
    titleContainer?: InjectableCss;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({
    id,
    view,
    // XNG-Change
    filterDrawerIsOpen,
    headerHeight,
    setFilterDrawerIsOpen,
    clearAllProductFilters,
    productFilters,
    isTabletScreen,
}) => {
    const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle =
        view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;

    // XNG-Changes
    useEffect(() => {
        if (filterDrawerIsOpen && !isTabletScreen) {
            handleCloseFilterDrawer();
        }
    }, [isTabletScreen]);

    const handleCloseFilterDrawer = () => {
        setFilterDrawerIsOpen({ filterDrawerIsOpen: false });
    };

    const hasActiveFilters = Object.entries(productFilters || {}).find(
        ([key, value]) => !["pageSize", "query", "page", "pageCategoryId", "pageBrandId"].includes(key) && !!value,
    );
    // </XNG-Changes>

    return (
        <GridContainer {...styles.container}>
            <GridItem {...leftColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content00" />
            </GridItem>
            <GridItem {...rightColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
            {/* XNG-Changes: add drawer for tablet & mobile screens, change structure */}
            <Drawer
                isOpen={filterDrawerIsOpen}
                handleClose={() => handleCloseFilterDrawer()}
                position="left"
                draggable={false}
                contentLabel="filter drawer"
                headline={
                    <StyledWrapper {...styles.titleContainer}>
                        <Typography {...styles.title}>{translate("Filter Results")}</Typography>
                        {hasActiveFilters && (
                            <Link
                                {...styles.closeBtn}
                                onClick={() => clearAllProductFilters()}
                                aria-label={translate("Clear Filters")}
                            >
                                {translate("Clear Filters")}
                            </Link>
                        )}
                    </StyledWrapper>
                }
                {...styles.filtersDrawer}
            >
                <StyledWrapper {...styles.filterWrapper} {...{ headerHeight }}>
                    <StyledWrapper {...styles.controlsWrapper}>
                        {createWidgetElement("ProductList/ProductListSortSelect", { fields: {} })}
                        {createWidgetElement("ProductList/ProductListPagination", {
                            fields: { showPagesNumber: false, showResultsPerPage: true },
                        })}
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Content00" />
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.cancelBtn}
                            onClick={() => {
                                handleCloseFilterDrawer();
                            }}
                            aria-label={translate("Close")}
                        >
                            {translate("Close")}
                        </Button>
                        <Button
                            {...styles.applyBtn}
                            onClick={handleCloseFilterDrawer}
                            aria-label={translate("Apply Filters")}
                            disabled={!hasActiveFilters}
                        >
                            {translate("Apply Filters")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Drawer>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Changes: add mapDispatchToProps
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
